/**
 * Website entry point.
 *
 * This file is the entry point for the website and is responsible for importing
 * the necessary assets, initializing the application, and fetching the application
 * docket from the API.
 *
 * @since 2.0.0
 * @version 3.0.0
 * @module index
 */

/**
 * Import JavaScript Assets
 * @since 3.0.0
 */
import { Cookies } from "commonAssets/cookies";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { Header } from "commonAssets/components/Header";
import { Navigation } from "commonAssets/components/Navigation";
import { Footer } from "commonAssets/components/Footer";
import { ImportantDates } from "commonAssets/components/ImportantDates";
import { siteTitle } from "commonAssets/lib/siteTitle.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { I18n } from "commonAssets/i18n";
import {
    loading,
    appDocketAPI,
    appClaimForm,
    siteInfoLang,
    userLanguage,
    setSiteInfo,
    setSiteInfoLang,
    setEmailInform,
    setPhoneAdmin,
    setEmailConfirm,
    setUserLanguage,
    setAppLocale,
    setI18n,
} from "commonAssets/js/constants";
import languages from "./languages";

/**
 * Import Styles
 * @since 3.2.3
 */
import "./style/custom.css";

/**
 * Import Custom JavaScript Script
 * @since 3.0.0
 */
import {
    customInitClaim,
    customProcessClaim,
    customLoadMember,
    customPage,
} from "./scripts/custom";
import {
    customSubmitClaim,
    valid_models,
} from "./scripts/SubmitClaim/index.mjs";
import { customSubmitFutureCoverageClaim } from "./scripts/SubmitFutureCoverageClaim/index.mjs";

// Initialize error handler
const errorHandler = new ErrorHandler();

function normalizeSerialNumber(value = "") {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function chunkModels(models, columns = 3) {
    const rows = [];

    for (let index = 0; index < models.length; index += columns) {
        rows.push(models.slice(index, index + columns));
    }

    return rows;
}

function isEligibleSerialNumber(value = "") {
    const serial = normalizeSerialNumber(value);

    if (/^HR\d{8}$/.test(serial)) {
        const numericPortion = Number.parseInt(serial.slice(2), 10);
        return numericPortion >= 81800000 && numericPortion <= 99999999;
    }

    if (/^HRX\d{7}$/.test(serial)) {
        return true;
    }

    if (/^HRA\d{7}$/.test(serial)) {
        const numericPortion = Number.parseInt(serial.slice(3), 10);
        return numericPortion >= 0 && numericPortion <= 3999999;
    }

    return false;
}

function mountEligibilityChecker() {
    const pageContent = document.querySelector(".page-content");
    const modelRows = chunkModels(valid_models);

    if (!pageContent || document.getElementById("eligibilityChecker")) {
        return;
    }

    const checker = document.createElement("section");
    checker.className = "eligibility-checker mt-4";
    checker.id = "eligibilityChecker";
    checker.innerHTML = `
        <div class="eligibility-checker__card mx-auto">
            <h3 class="eligibility-checker__title">Serial Number Checker</h3>
            <p class="eligibility-checker__copy">
                Enter your refrigerator serial number to see whether it is covered under the Settlement.
            </p>
            <p class="mt-2">
                Note: To locate the serial number, look for the tag on the inside frame of your refrigerator door. Be sure to carefully review your serial number.
            </p>
            <form class="eligibility-checker__form mt-3" novalidate>
                <div class="eligibility-checker__controls">
                    <input
                        class="eligibility-checker__input form-control"
                        id="eligibilitySerialNumber"
                        name="eligibilitySerialNumber"
                        type="text"
                        autocomplete="off"
                        aria-describedby="eligibilityCheckerHelp eligibilityCheckerResult"
                        placeholder="Enter Serial Number"
                    />
                    <button class="btn btn-primary" type="submit">Check Eligibility</button>
                </div>
                <div
                    class="eligibility-checker__result"
                    id="eligibilityCheckerResult"
                    aria-live="polite"
                ></div>
            </form>
            <div class="eligibility-checker__models mt-4">
                <h4 class="eligibility-checker__models-title">
                    Eligible Models
                </h4>
                <div class="eligibility-checker__table-wrap mt-3">
                    <table class="eligibility-checker__table table table-sm mb-0">
                        <tbody>
                            ${modelRows
                                .map(
                                    (row) => `
                                        <tr>
                                            ${row
                                                .map(
                                                    (model) => `
                                                        <td>${model}</td>
                                                    `
                                                )
                                                .join("")}
                                            ${Array.from({
                                                length: 3 - row.length,
                                            })
                                                .map(() => "<td></td>")
                                                .join("")}
                                        </tr>
                                    `
                                )
                                .join("")}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    `;

    const form = checker.querySelector("form");
    const input = checker.querySelector("#eligibilitySerialNumber");
    const result = checker.querySelector("#eligibilityCheckerResult");

    form.addEventListener("submit", (event) => {
        event.preventDefault();

        const rawValue = input.value.trim();
        const normalizedSerial = normalizeSerialNumber(rawValue);

        result.className = "eligibility-checker__result";

        if (!rawValue) {
            result.textContent = "Enter a serial number to check eligibility.";
            result.classList.add("eligibility-checker__result--invalid");
            return;
        }

        input.value = normalizedSerial;

        if (isEligibleSerialNumber(normalizedSerial)) {
            result.textContent =
                "The Serial Number provided is covered under the Settlement. You may submit a claim form.";
            result.classList.add("eligibility-checker__result--valid");
            return;
        }

        result.textContent =
            "The Serial Number provided is not covered under the Settlement.";
        result.classList.add("eligibility-checker__result--invalid");
    });

    pageContent.appendChild(checker);
}

/**
 * Internationalization (i18n) Handling
 *
 * Sets the langauges available for the distribution.
 *
 * Examples:
 * i18n.languages returns languages in an array
 * i18n.userLanguage returns current languages
 * i18n.getTranslation('string') returns predefined string
 */
const i18n = I18n.create(["en", "es"], {}, languages);
setI18n(() => i18n);

/**
 * Cookies
 * @returns {void}
 */
const set_cookies = new Cookies();
set_cookies.init();

/**
 * Set the user's language using the value from `set_cookies.language()`.
 * @param {Function} setCookiesLanguage - The function that returns the user's language.
 */
setUserLanguage(() => set_cookies.language());

/**
 * Set the application locale using the value from `set_cookies.locale()`.
 * @param {Function} setCookiesLocale - The function that returns the application locale.
 */
setAppLocale(() => set_cookies.locale());

/**
 * Loading GIF
 */
loadingGif(loading, "show");

/**
 * Initialize the analytics by loading the Google Tag Manager script and
 * configuring the dataLayer and gtag.
 */
export function analytics() {
    $.getScript(
        "https://www.googletagmanager.com/gtag/js?id=G-LGF2TN8VN9",
        () => {
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                dataLayer.push(arguments);
            }
            gtag("js", new Date());

            gtag("config", "G-WKXC7VDFFT"); // swatjs
            gtag("config", "G-LGF2TN8VN9"); // general

            // Add additional `gtag`s here.
            gtag('config', 'G-XJTY8X86XJ');
        }
    );

    // Add custom pixels/analytics code here.
}

/**
 * Calls the appropriate function to display page content based on the given domainSlug.
 * If domainSlug is empty, the 'home' case will be executed.
 *
 * @param {Array} sitePages - An array containing the website sitePages' information.
 * @param {string} domainSlug - The unique identifier (slug) of the page to be displayed.
 * @param {} importantDates -
 *
 * @example
 * // Assuming sitePages array and domainSlug are defined and valid
 * getPage(sitePages, domainSlug, importantDates)
 */
export async function getPage(sitePages, domainSlug, importantDates) {
    // Show the loading gif
    loadingGif(loading, "show");

    // Set the site title
    if (domainSlug === "") {
        siteTitle(sitePages);
    } else {
        siteTitle(sitePages, domainSlug);
    }

    // Misc handling.
    domainSlug = domainSlug || "home";

    // Create a new page instance
    const page = new customPage(sitePages, domainSlug, importantDates);
    switch (domainSlug) {
        case "home":
            page.home();
            analytics();
            break;

        case "submit-claim":
            // Use test mode for local development to bypass API deadline checks
            // This allows developers to test HTML partials without API dependencies
            const isLocalDevelopment = window.location.hostname === 'localhost';

            let SubmitClaimClass = customSubmitClaim;

            // Dynamically import test class only in localhost to avoid bundling in production
            if (isLocalDevelopment) {
                try {
                    const { testSubmitClaim } = await import("./scripts/SubmitClaim/testSubmitClaim.mjs");
                    SubmitClaimClass = testSubmitClaim;
                    console.log("Using test mode for localhost development");
                } catch (error) {
                    console.warn("Failed to load test mode, falling back to production mode:", error);
                    // Fall back to production class if test import fails
                }
            }

            const submit_claim = SubmitClaimClass.createInstance({
                appClaimForm,
                userLanguage,
                customInitClaim,
                customProcessClaim,
                customLoadMember,
                errorHandler,
            });
            submit_claim.form();
            analytics();
            gtag('event', 'conversion', {'send_to': 'AW-10839088199/S-HVCOTLw5ADEMe4vbAo'});
            break;

        case "submit-future-coverage-claim": {
            const isLocalDev = window.location.hostname === 'localhost';

            let FutureCoverageClass = customSubmitFutureCoverageClaim;

            if (isLocalDev) {
                try {
                    const { testFutureCoverageClaim } = await import("./scripts/SubmitFutureCoverageClaim/testFutureCoverageClaim.mjs");
                    FutureCoverageClass = testFutureCoverageClaim;
                    console.log("Using test mode for localhost development");
                } catch (error) {
                    console.warn("Failed to load test mode, falling back to production mode:", error);
                }
            }

            const submit_future_coverage_claim = FutureCoverageClass.createInstance({
                appClaimForm,
                userLanguage,
                customInitClaim,
                customProcessClaim,
                customLoadMember,
                errorHandler,
            });
            submit_future_coverage_claim.form();
            analytics();
            gtag('event', 'conversion', {'send_to': 'AW-10839088199/S-HVCOTLw5ADEMe4vbAo'});
            break;
        }

        case "check-eligibility":
            page.default();
            mountEligibilityChecker();
            analytics();
            break;

        case "important-documents":
            page.important_documents();
            analytics();
            break;

        case "faqs":
            page.faqs();
            analytics();
            break;

        case "contact-us":
            page.contact();
            analytics();
            break;

        default:
            page.error();
            analytics();
    }
}

/**
 * Fetches the application docket from the API and updates the site information.
 *
 * This function performs the following steps:
 * 1. Fetches data from the `appDocketAPI`.
 * 2. Checks the response status and handles errors if the status is greater than 299.
 * 3. Parses the response JSON data.
 * 4. Updates site information such as language, email, phone, and title.
 * 5. Initializes and updates the header, navigation, footer, and important dates on the page.
 *
 * @async
 * @function fetchAppDocket
 * @throws {Error} Throws an error if the API response status is greater than 299 or if there is an error during the fetch operation.
 */

async function fetchAppDocket() {
    try {
        // Fetch data from the app docket API
        const response = await fetch(appDocketAPI);

        // Check the response status and handle errors
        if (response.status > 299) {
            // This error will init the catch block
            throw new Error("App Docket API Error", { cause: response });
        }

        // Parse the response JSON data
        const data = await response.json();

        // Update site information
        setSiteInfoLang(() => data.findResult.lang[userLanguage]);
        setSiteInfo(() => data.findResult);
        setEmailInform(() => data.findResult.email_info);
        setPhoneAdmin(() => data.findResult.phone);
        setEmailConfirm(() => data.findResult.email_conf);

        // Update the document title
        document.title = siteInfoLang.SiteTitle;

        // Domain slug and site pages info
        const domainSlug = window.location.pathname.split("/")[1];
        const sitePages = siteInfoLang.Pages;
        sitePages.splice(2, 0, {
            PageName: "submit-future-coverage-claim",
            PageTitle: "Submit Future Coverage Claim"
        });
        console.log(sitePages);
        

        // Initialize the header, footer, and important dates
        const header = new Header(
            siteInfoLang.SiteName,
            siteInfoLang.CaseNumber,
            siteInfoLang.CourtName
        );
        const importantDates = new ImportantDates(data);
        const navigation = new Navigation(sitePages, domainSlug, getPage);
        const footer = new Footer();

        // Header and navigation
        const headerElem = document.querySelector("header");
        headerElem.classList.add("py-3");
        headerElem.appendChild(header.createHeader());

        // Navigation
        const nav_html = navigation.element.outerHTML;
        navigation.pathChange(sitePages, domainSlug, importantDates);

        document
            .querySelector("header")
            .insertAdjacentHTML("afterend", nav_html);
        document.querySelector("footer").appendChild(footer.element);

        // Get the page content
        await getPage(sitePages, domainSlug, importantDates);
    } catch (error) {
        // Log and handle errors
        errorHandler.log_exception(
            new Error("Error fetching app docket", { cause: error })
        );
        errorHandler.display(error);

        // Hide the loading gif
        loadingGif(loading, "hide", 1000);
    }
}

// Fetch the application docket when the DOM content is loaded
document.addEventListener("DOMContentLoaded", fetchAppDocket);
